// R1c — Legacy AppShell now delegates to AdminAppShell so every page that
// still imports "../../components/AppShell" (CRM, Projects, Reports, Help,
// Estimates, Proposals, Deposits, Contracts, LeadDetail) rehomes onto the
// new design-system shell in one line-change. This preserves the `actions`
// slot pages already use, and auto-derives `activeNav` + breadcrumbs from
// the current pathname so no per-page rewiring is needed.
import React from "react";
import { useLocation } from "react-router-dom";
import { AdminAppShell } from "../design-system/shells/AdminAppShell";
import { useAuth } from "../lib/auth";

function deriveActiveNav(pathname) {
  if (pathname.startsWith("/crm")) return "crm";
  if (pathname.startsWith("/projects")) return "operations";
  if (pathname.startsWith("/reports")) return "reports";
  if (pathname.startsWith("/help") || pathname.startsWith("/learn")) return "help";
  if (pathname.startsWith("/admin/people-roles")) return "people-access";
  if (pathname.startsWith("/admin/financial")) return "financial";
  if (pathname.startsWith("/admin/operations")) return "operations";
  if (pathname.startsWith("/admin/audit-trust") || pathname.startsWith("/admin/audit")) return "audit-trust";
  if (pathname.startsWith("/admin/integrations")) return "integrations";
  return "command";
}

const CRUMB_LABELS = {
  "crm": "CRM",
  "leads": "Leads",
  "opportunities": "Opportunities",
  "estimates": "Estimates",
  "proposals": "Proposals",
  "contracts": "Contracts",
  "deposits": "Deposits",
  "contacts": "Contacts",
  "pipeline": "Pipeline",
  "activities": "Activities",
  "projects": "Projects",
  "reports": "Reports",
  "help": "Help Center",
  "learn": "Learn Hub",
};

function deriveBreadcrumbs(pathname) {
  const parts = pathname.split("/").filter(Boolean);
  if (!parts.length) return [];
  const crumbs = [{ label: "Owner Command Center", to: "/owner" }];
  let path = "";
  for (let i = 0; i < parts.length; i++) {
    const seg = parts[i];
    path += `/${seg}`;
    // Skip UUIDs / opaque IDs — they'll be rendered by the page's own title.
    if (i > 0 && seg.length > 10 && /[a-f0-9-]{10,}/i.test(seg)) continue;
    const label = CRUMB_LABELS[seg] || seg.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    const isLast = i === parts.length - 1;
    crumbs.push({ label, to: isLast ? null : path });
  }
  return crumbs;
}

export default function AppShell({ children, title, subtitle, actions }) {
  const { user } = useAuth();
  const location = useLocation();
  const activeNav = deriveActiveNav(location.pathname);
  const breadcrumbs = deriveBreadcrumbs(location.pathname);
  return (
    <AdminAppShell
      activeNav={activeNav}
      title={title}
      subtitle={subtitle}
      breadcrumbs={breadcrumbs}
      user={user}
    >
      {actions && (
        <div
          className="flex flex-wrap items-center justify-end gap-2 mb-4"
          data-testid="app-shell-actions"
        >
          {actions}
        </div>
      )}
      {children}
    </AdminAppShell>
  );
}
